/**
 * Redux store configuration with persistence support.
 * 配置可持久化的 Redux store。
 */
import { configureStore, combineReducers } from '@reduxjs/toolkit';
import  userReducer  from './slices/userSlice';
import  idolReducer, { getIdolArray }  from './slices/idolSlice';
import  walletReducer  from './slices/walletSlice';
import  notificationReducer  from './slices/notificationSlice';
import  chatReducer  from './slices/chatSlice';
import  appReducer  from './slices/appSlice';
import  onlineReducer  from './slices/onlineSlice';

import { setAxiosStore } from '@/shared/api/axios';
import { persistStore, persistReducer, createMigrate } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

const migrations = {
  1: (state) => ({
    ...state,
    idol: {
      ...state.idol,
      list: Array.isArray(state.idol?.list) ? state.idol.list : [],
    },
  }),
  // 通知 title/content 由純字串改為多語物件 { zh_tw, en, jp }，
  // 清空舊持久化資料避免殘留舊格式（App 掛載時會重新 fetch）
  2: (state) => ({
    ...state,
    notification: {
      notifications: [],
      unreadCount: 0,
      total: 0,
      page: 1,
      hasMore: true,
      isLoading: false,
    },
  }),
  // wallet 不再持久化（見下方 rootReducer），
  // 丟掉 persist:root 內殘留的舊 wallet blob，避免升級後第一次載入又讀回過期的餘額與錯誤
  3: (state) => {
    const next = { ...state };
    delete next.wallet;
    return next;
  },
  // idol.status 沒有任何讀取端，已移除；丟掉持久化資料內殘留的舊值（可能卡在 'loading'）
  4: (state) => {
    const idol = { ...state.idol };
    delete idol.status;
    return { ...state, idol };
  },
  // chat 改由 chatPersistConfig 獨立持久化（只存 rooms、imageUrl），丟掉 persist:root 內殘留的整包 chat；
  // 聊天室列表在 App 掛載時會重抓
  5: (state) => {
    const next = { ...state };
    delete next.chat;
    return next;
  },
};

// Configuration for redux-persist
// redux-persist 的相關設定
const persistConfig = {
  key: 'root',
  storage,
  blacklist: ['app', 'wallet', 'online', 'chat'],
  version: 5,
  migrate: createMigrate(migrations, { debug: false }),
}

// chat 只持久化聊天室列表（重新整理後先顯示舊列表），其餘是本次 session 的暫態：
// 整包持久化時，重新整理會還原 typingIdolId、incomingMessage、loading，「輸入中」可能一直卡著
const chatPersistConfig = {
  key: 'chat',
  storage,
  whitelist: ['rooms', 'imageUrl'],
}

// Combine reducers for user and idol data
// 將 user 與 idol reducer 合併
//
// wallet 完全不持久化（已列在上方 persistConfig 的 blacklist）：
// 裡面沒有任何欄位需要跨 session 保留——balance 在錢包頁、解鎖貼文、訂閱確認三處
// 開啟時都會重抓，其餘三個金額欄位全站無消費端；loading 更是暫態。
// 一旦持久化，重新整理後會先 rehydrate 出上一個 session 的舊餘額，
// 消費端的「null＝還沒拿到」判斷就會失效，使用者可能看著過期數字決定要不要扣款。
// 注意這只擋掉「重新整理」那一半；登出／換偶像走的是 SPA 導向、不會重載頁面，
// 那一半由 walletSlice 監聽 setIdolId / setCurrentIdol / clearSession 歸零負責。
const rootReducer = combineReducers({
  user: userReducer,
  idol: idolReducer,
  wallet: walletReducer,
  notification: notificationReducer,
  chat: persistReducer(chatPersistConfig, chatReducer),
  app: appReducer,
  // 偶像上線狀態（見 docs/idol-online-plan.md §4.2），即時資料，不持久化（已列在 blacklist）
  online: onlineReducer,
})

// Wrap reducers with persistence capabilities
// 將 reducer 包裝以啟用狀態持久化
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Create and export the Redux store
// 建立並匯出 Redux store
export const store = configureStore({
  // reducer: {
  //   user: userReducer
  // },
  reducer: persistedReducer,
  devTools: import.meta.env.DEV,
  // 使用 RTK 內建的 middleware（thunk 由 @reduxjs/toolkit 帶入，不另外宣告 redux-thunk 依賴）。
  // 開發環境的 serializable／immutable 檢查維持關閉（redux-persist 的 action 帶函式，開啟需另設 ignoredActions）；
  // actionCreatorCheck 保留預設開啟：只在開發環境、誤寫 dispatch(actionCreator) 時 console.warn，正式站不載入
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({
    serializableCheck: false,
    immutableCheck: false,
  }),
})

// 把 store 注入 axios 攔截器（取 token、清登入狀態、標記網路錯誤都需要）。
// 依賴方向刻意是 store → axios 單向，axios 不反過來 import store，避免循環 import。
// refetchIdolArray 供 IDOL_3044 重抓偶像列表（idolSlice 會 import axios，同樣改用注入）。
setAxiosStore(store, {
  refetchIdolArray: () => store.dispatch(getIdolArray()),
});

// Export the persistor used by redux-persist
// 匯出給 redux-persist 使用的 persistor
export const persistor = persistStore(store)